"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = void 0;
const edit_1 = __importDefault(require("@sheet/edit"));
const common_1 = require("@finley-technologies/common");
const consolidateErrors_1 = require("./consolidateErrors");
const errorUtils_1 = require("./errorUtils");
const helpers_1 = require("./helpers");
const readWorkbook_1 = require("./readWorkbook");
const typeguards_1 = require("./typeguards");
const consolidateFieldErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.fieldName, // Group by field
    (error) => ({
        message: error.message,
        sheetName: error.sheetName,
    }), // Map to row data
    (field) => `Field [${field}] had errors`);
};
const consolidateCellErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.fieldName, // Group by field
    (error) => ({
        message: error.message,
        sheetName: error.sheetName,
        cell: error.cell,
    }), // Map to row data
    (field) => `Field [${field}] had errors`);
};
const consolidateToStructuredErrors = (errors) => {
    const fieldErrors = errors.filter((error) => error.type === 'xlsx2CsvFieldError');
    const cellErrors = errors.filter((error) => error.type === 'xlsx2CsvCellError');
    // we want to consolidate the errors if there are too many of those.
    return [
        ...consolidateFieldErrors(fieldErrors),
        ...consolidateCellErrors(cellErrors),
    ];
};
const isValidRange = (range) => {
    const { s: start, e: end } = range;
    return start.c >= 0 && start.r >= 0 && end.c >= start.c && end.r >= start.r;
};
const getDecodedSheetRange = (sheet) => {
    // sheet['!ref'] property represents the worksheet’s range,
    // but unfortunately it is not reliable and may include empty or unused cells,
    // leading to unexpectedly large values for sheetRange.e.r.
    // the caller need to be aware of this.
    const sheetRangeRefA1 = sheet['!ref'];
    if (!sheetRangeRefA1) {
        return null;
    }
    const sheetRangeRefDecoded = edit_1.default.utils.decode_range(sheetRangeRefA1);
    return sheetRangeRefDecoded;
};
// sheet['!ref'] property represents the worksheet's range,
// but unfortunately it is not reliable and may include empty or unused cells,
// leading to unexpectedly large values for sheetRange.e.r.
// https://github.com/SheetJS/sheetjs/issues/1601
// we use this workaround to find the last row with data.
const getLastDataRowIndex = ({ sheet, decodedHeaderRange, }) => {
    let maxRow = -1;
    const isColumnInRange = (col) => {
        return col >= decodedHeaderRange.s.c && col <= decodedHeaderRange.e.c;
    };
    // Support both sparse and dense sheet formats
    if ((0, readWorkbook_1.isDenseSheet)(sheet)) {
        // Dense format - supports both:
        // - Newer SheetJS (0.19.0+): sheet['!data'][row][col]
        // - Older @sheet/edit (1.x): sheet[rowIndex][col] where rowIndex is string '0', '1', etc.
        // Create array of column indices within header range
        // e.g., if header spans columns C to F (indices 2-5), this creates [2, 3, 4, 5]
        const startCol = decodedHeaderRange.s.c;
        const endCol = decodedHeaderRange.e.c;
        const columnIndices = Array.from({ length: endCol - startCol + 1 }, (_, i) => startCol + i);
        // Get row data based on format
        const getRow = (rowIndex) => {
            if (sheet['!data'] != null) {
                return sheet['!data'][rowIndex];
            }
            // Older format - rows are stored as sheet['0'], sheet['1'], etc.
            const row = sheet[String(rowIndex)];
            return Array.isArray(row) ? row : undefined;
        };
        // Find max row index by checking which numeric keys exist
        let maxRowToCheck = -1;
        if (sheet['!data'] != null) {
            maxRowToCheck = sheet['!data'].length - 1;
        }
        else {
            // For older format, find the highest numeric key
            Object.keys(sheet).forEach((key) => {
                const num = parseInt(key, 10);
                if (!Number.isNaN(num) && Array.isArray(sheet[key])) {
                    maxRowToCheck = Math.max(maxRowToCheck, num);
                }
            });
        }
        // Iterate from last row backwards, find first row with data in range
        // eslint-disable-next-line no-restricted-syntax, no-plusplus
        for (let rowIndex = maxRowToCheck; rowIndex >= 0; rowIndex--) {
            const row = getRow(rowIndex);
            if (row) {
                const hasDataInRange = columnIndices.some((colIndex) => row[colIndex] != null);
                if (hasDataInRange) {
                    maxRow = rowIndex;
                    break;
                }
            }
        }
    }
    else {
        // Sparse format: iterate through object keys
        Object.keys(sheet).forEach((cellAddress) => {
            if (cellAddress[0] === '!')
                return; // Skip special properties
            // Decode the cell address into row and column numbers
            const cell = edit_1.default.utils.decode_cell(cellAddress);
            if (cell && isColumnInRange(cell.c)) {
                maxRow = Math.max(maxRow, cell.r);
            }
        });
    }
    return maxRow;
};
const isWithinRange = ({ cellRangeA1, range, }) => {
    const decodedCellRange = edit_1.default.utils.decode_range(cellRangeA1);
    return (decodedCellRange.s.c >= range.s.c &&
        decodedCellRange.s.r >= range.s.r &&
        decodedCellRange.e.c <= range.e.c &&
        decodedCellRange.e.r <= range.e.r);
};
const getCellValue = ({ sheet, cellAddressA1, importFormat, }) => {
    // Support both sparse and dense sheet formats
    const decodedCell = edit_1.default.utils.decode_cell(cellAddressA1);
    const cellValue = (0, readWorkbook_1.getSheetCell)(sheet, decodedCell.r, decodedCell.c);
    if (!cellValue) {
        return {
            preSanitized: false,
            value: '', // return empty value for invalid cell.
        };
    }
    // SheetJS does not evaluate formulas on its own. It simply extracts the precalculated result from the file.
    // If there is no precalculated result in the file, cellValue.w will NOT have correct value.
    // https://github.com/SheetJS/sheetjs/issues/481
    // This can happen when the Excel file was saved programmatically (like servicer report generated by Finley),
    // in those cases precalculated result won't be in the file. The file needs to be opened in Excel and saved manually.
    // before using it as input to this function. We need to figure out that file is not precalculated and throw an error.
    let value;
    switch (importFormat) {
        case 'raw':
            value = cellValue.v;
            break;
        case 'formula':
            // read the formula value if it is available, if not use raw value.
            // this is what our xlsx2csv2 config expects.
            value = cellValue.f ?? cellValue.v;
            break;
        case 'formatted':
            value = cellValue.w ?? cellValue.v;
            break;
        default:
            (0, common_1.finleyAssertUnreachable)(importFormat);
    }
    if (value instanceof Date) {
        // Excel stores dates as floating point numbers.
        //	Integer Part: Represents the number of days since epoch
        //  Fractional Part: Represents the time of day as a fraction of a 24‑hour period (for example, 0.5 corresponds to noon).
        // When its date with no time (“1/10/23”) it represents
        // mid-night in local time for that date value.
        // Because of floating point precision issues,
        // the time may be slightly off.
        // I saw "1/10/23" read as "1/9/23 11:59:50 PM" when ran in india.
        // I also saw "1/10/23" read as 'Mon Jan 09 2023 23:00:00 GMT-0800 (Pacific Standard Time)' when run in seattle on
        // Mar 10, 2025 (which is PST)
        // To ensure that date is well within new day, we will add 1 hour + 1 second to it.
        const ONE_HOUR = 60 * 60 * 1000;
        const ONE_SECOND = 1000;
        const ADJUSTMENT = ONE_HOUR + ONE_SECOND;
        // Note: we support dateTime as one of our sanitize formats.
        //  it is not very common for our data tapes to care about time values.
        //  but this padding of time is definitely a hack, and may cause issues
        //  if we really cared for accurate dateTime.
        // TODO: revisit this when we update @sheet/edit version.
        const paddedDateTime = new Date(value.getTime() + ADJUSTMENT);
        return {
            preSanitized: true,
            value: common_1.date.dateToLocalFullDateString(paddedDateTime),
        };
    }
    return {
        preSanitized: false,
        value,
    };
};
// we allow to specify header in multiple rows.
// we will walk the headers and create a map of value => column indexes.
const generateHeaderToColumnsMap = (decodedHeaderRange, sheet) => {
    const headerValueToColumns = new Map();
    // eslint-disable-next-line no-restricted-syntax
    for (let columnIndex = decodedHeaderRange.s.c; columnIndex <= decodedHeaderRange.e.c; 
    // eslint-disable-next-line no-plusplus
    columnIndex++) {
        // eslint-disable-next-line no-restricted-syntax
        for (let rowIndex = decodedHeaderRange.s.r; rowIndex <= decodedHeaderRange.e.r; 
        // eslint-disable-next-line no-plusplus
        rowIndex++) {
            const cellAddressA1 = edit_1.default.utils.encode_cell({
                c: columnIndex,
                r: rowIndex,
            });
            const cellValue = getCellValue({
                sheet,
                cellAddressA1,
                importFormat: 'raw',
            });
            const { value: rawColumnValue } = cellValue;
            const columnIndices = headerValueToColumns.get(rawColumnValue) || new Set();
            columnIndices.add(columnIndex);
            headerValueToColumns.set(rawColumnValue, columnIndices);
        }
    }
    return headerValueToColumns;
};
const getMatchingSheet = ({ workbook, sheetName, isSheetNameRegex, }) => {
    const sheetNames = workbook.SheetNames;
    if (isSheetNameRegex) {
        // sheetName is a regex, we need to find the sheet that matches the regex
        const matchingSheetNames = sheetNames.filter((name) => new RegExp(sheetName).test(name));
        (0, common_1.finleyAssert)(matchingSheetNames.length !== 0, `No matching sheet found for regular expression [${sheetName}]`);
        // ensure that just one name matches the regex
        (0, common_1.finleyAssert)(matchingSheetNames.length === 1, `Expected 1 matching sheet for regular expression [${sheetName}], found multiple [${matchingSheetNames.join(', ')}]`);
        const sheet = workbook.Sheets[matchingSheetNames[0]];
        return sheet;
    }
    (0, common_1.finleyAssert)(sheetNames.includes(sheetName), `Sheet with name [${sheetName}] not found in workbook`);
    return workbook.Sheets[sheetName];
};
const validateRegEx = (regex) => {
    try {
        // eslint-disable-next-line no-new
        new RegExp(regex);
    }
    catch (err) {
        throw new Error(`Invalid regex: ${regex}`);
    }
};
const validateSheetName = ({ sheetName, isSheetNameRegex, }) => {
    if (isSheetNameRegex) {
        validateRegEx(sheetName);
    }
};
const validateExtractValuesConfig = (transformation) => {
    const { fields } = transformation;
    (0, common_1.finleyAssert)(fields.length > 0, 'fields must have at least one entry');
    let fieldCellCount = null;
    const uniqueFieldNames = new Set();
    fields.forEach((field) => {
        const { cell, cellRange, field: fieldName, sheetName, isSheetNameRegex, } = field;
        (0, common_1.finleyAssert)(!cell || !cellRange, `[field:${fieldName}] must not specify both cell and cellRange`);
        const effectiveCellRange = cellRange ?? cell;
        (0, common_1.finleyAssert)(effectiveCellRange, `[field:${fieldName}] must specify cell or cellRange`);
        validateSheetName({ sheetName, isSheetNameRegex });
        const decodedCellRange = edit_1.default.utils.decode_range(effectiveCellRange);
        const { s: start, e: end } = decodedCellRange;
        (0, common_1.finleyAssert)(start.c >= 0 && start.r >= 0, `[field:${fieldName}] Invalid [columnIndex:${start.c}][rowIndex:${start.r}] for [cellAddress:${cell}]`);
        const rangeCellCount = (end.r - start.r + 1) * (end.c - start.c + 1);
        if (!fieldCellCount) {
            fieldCellCount = {
                fieldName,
                cellCount: rangeCellCount,
            };
        }
        else {
            (0, common_1.finleyAssert)(fieldCellCount.cellCount === rangeCellCount, `All fields must have same number of values. field [${fieldCellCount.fieldName}] has [cellCount:${fieldCellCount.cellCount}] but field [${fieldName}] has [cellCount:${rangeCellCount}]`);
        }
        (0, common_1.finleyAssert)(!uniqueFieldNames.has(fieldName), `Duplicate field name: ${fieldName}`);
        uniqueFieldNames.add(fieldName);
        // @xlsx2Csv:TODO: validate field (date must be in yyyy-mm-dd format)
        // this is because we get date/times as js objects.
    });
};
const getOutputDataTapeValues = (transformation) => {
    validateExtractValuesConfig(transformation);
    const baseFields = transformation.fields.map((fieldDef) => {
        const { sheetName: _unUsed1, isSheetNameRegex: _unUsed2, cell: _unUsed3, importFormat: _unUsed4, ...rest } = fieldDef;
        return rest;
    });
    // output tape definition is decided by sanitize.
    // lets call sanitize helper to get the output tape definition.
    const sanitizeTransformation = {
        operation: 'sanitize',
        srcDataTape: {
            baseFields,
            delimiter: ',',
        },
        // Note:we do not want to sanitize field names
        // to give flexibility to user to decorate them with special characters.
        // if they wanted sanitized names, they can always specify fields names as such.
        sanitizeFieldNames: false,
    };
    return (0, helpers_1.getSanitizeTransformOpOutputDataTape)(sanitizeTransformation);
};
const doTransformValues = async ({ xlsBuffer, transformation, }) => {
    const { fields } = transformation;
    const workbook = await (0, readWorkbook_1.readWorkbook)(xlsBuffer, {
        useDenseMode: !!transformation.useDenseMode,
    });
    const errors = [];
    const fieldValues = fields.map((entry) => {
        const sanitizedValues = [];
        const { cell, cellRange, sheetName, field, importFormat, isSheetNameRegex, } = entry;
        const pushNewError = (message, cellValue) => {
            if (cellValue) {
                errors.push({
                    type: 'xlsx2CsvCellError',
                    sheetName,
                    cell: cellValue,
                    fieldName: field,
                    message,
                });
            }
            else {
                errors.push({
                    type: 'xlsx2CsvFieldError',
                    sheetName,
                    fieldName: field,
                    message,
                });
            }
        };
        try {
            const sheet = getMatchingSheet({
                workbook,
                sheetName,
                isSheetNameRegex: !!isSheetNameRegex,
            });
            const sheetRange = getDecodedSheetRange(sheet);
            if (!sheetRange) {
                pushNewError(`[sheet:${sheetName}] is empty.`);
                return {
                    field,
                    sanitizedValues,
                };
            }
            const effectiveCellRange = cellRange ?? cell;
            (0, common_1.finleyAssert)(effectiveCellRange, `[field:${field}] must specify cell or cellRange`);
            const decodedCellRange = edit_1.default.utils.decode_range(effectiveCellRange);
            const { s: start, e: end } = decodedCellRange;
            // eslint-disable-next-line no-restricted-syntax, no-plusplus
            for (let rowIndex = start.r; rowIndex <= end.r; rowIndex++) {
                // eslint-disable-next-line no-restricted-syntax, no-plusplus
                for (let columnIndex = start.c; columnIndex <= end.c; columnIndex++) {
                    const cellAddressA1 = edit_1.default.utils.encode_cell({
                        c: columnIndex,
                        r: rowIndex,
                    });
                    try {
                        if (!isWithinRange({
                            cellRangeA1: effectiveCellRange,
                            range: sheetRange,
                        })) {
                            pushNewError(`[cell:${effectiveCellRange}] is out of sheet range.`, cellAddressA1);
                            return {
                                field,
                                sanitizedValues,
                            };
                        }
                        const cellValue = getCellValue({
                            sheet,
                            cellAddressA1,
                            importFormat,
                        });
                        const { preSanitized, value } = cellValue;
                        // sanitize the field
                        const sanitizedValue = (0, helpers_1.getSanitizedDataTapeField)(entry, value, preSanitized);
                        sanitizedValues.push(sanitizedValue);
                    }
                    catch (err) {
                        if ((0, typeguards_1.isInvalidField)(err)) {
                            // sanitize throws this kind of error convert it to xlsx2CsvInternalError
                            const message = `${err.name}: with [value:${err.value}]. Expected type: ${err.expectedType}`;
                            pushNewError(message, cellAddressA1);
                            return {
                                field,
                                sanitizedValues,
                            };
                        }
                        // for all other errors push them as is.
                        pushNewError(common_1.error.getErrorMessage(err), cellAddressA1);
                    }
                }
            }
        }
        catch (err) {
            pushNewError(common_1.error.getErrorMessage(err));
        }
        return {
            field,
            sanitizedValues,
        };
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    // generate csvArray from the array of sanitized values.
    const rows = fieldValues.length ? fieldValues[0].sanitizedValues.length : 0;
    const csvArray = [];
    // eslint-disable-next-line no-restricted-syntax, no-plusplus
    for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
        const row = fieldValues.reduce((acc, { field, sanitizedValues }) => {
            // eslint-disable-next-line no-param-reassign
            acc[field] = sanitizedValues[rowIndex];
            return acc;
        }, {});
        csvArray.push(row);
    }
    return {
        isValid: true,
        data: {
            outputBuffer: common_1.csvConversion.csvArrayToFinleyBuffer({
                csvArray,
                newline: '\n', // arrayToCsvBuffer uses \r\n by default
            }),
        },
        supplementals: [],
    };
};
const validateRangeConfig = (transformation) => {
    const { fields, sheetName, isSheetNameRegex } = transformation;
    validateSheetName({
        sheetName,
        isSheetNameRegex,
    });
    (0, common_1.finleyAssert)(fields.length > 0, 'fields must have at least one entry');
    // validate that headerRange if specified is a single row
    // A1 notation.
    const { headerRange, dataEndRange } = transformation;
    // we expect headerRange of the format A1:Z1
    // unfortunately XLSX.utils.decode_range does not throw an error
    // for single cell input like A1Z1
    (0, common_1.finleyAssert)(headerRange.includes(':'), `headerRange:${headerRange} is not valid. It must be of the format A1:Z1`);
    const decodedHeaderRange = edit_1.default.utils.decode_range(headerRange);
    (0, common_1.finleyAssert)(isValidRange(decodedHeaderRange), `[headerRange:${headerRange}] is not valid.`);
    const { s: headerStart, e: headerEnd } = decodedHeaderRange;
    if (dataEndRange) {
        // we expect dataEndRange of the format A5:Z5
        // unfortunately XLSX.utils.decode_range does not throw an error
        // for single cell input like A1Z1
        (0, common_1.finleyAssert)(dataEndRange.includes(':'), `dataEndRange:${dataEndRange} is not valid. It must be of the format A5:Z5`);
        const decodedDataEndRange = edit_1.default.utils.decode_range(dataEndRange);
        (0, common_1.finleyAssert)(isValidRange(decodedDataEndRange), `[dataEndRange:${dataEndRange}] is not valid.`);
        const { s: dataEndRangeStart, e: dataEndRangeEnd } = decodedDataEndRange;
        (0, common_1.finleyAssert)(dataEndRangeStart.r === dataEndRangeEnd.r, `[dataEndRange:${dataEndRange}] must be a single row range`);
        (0, common_1.finleyAssert)(dataEndRangeStart.c === headerStart.c &&
            dataEndRangeEnd.c === headerEnd.c, `[dataEndRange:${dataEndRange}] must have same columns as [headerRange:${headerRange}]`);
        (0, common_1.finleyAssert)(dataEndRangeStart.r > headerEnd.r, `[dataEndRange:${dataEndRange}] must be after [headerRange:${headerRange}]`);
    }
    // Sanitized field name validations
    const outputFields = new Map(); // key: output field, value: input field
    // If sanitizeFieldNames is enabled, then this additional check for input field handling should be performed
    fields.forEach((field) => {
        const { field: fieldName, columnToReadFrom } = field;
        const inputField = columnToReadFrom ?? fieldName;
        const outputField = (0, helpers_1.getSanitizedFieldName)(fieldName, Boolean(transformation.sanitizeFieldNames));
        const matchingInputField = outputFields.get(outputField);
        (0, common_1.finleyAssert)(!matchingInputField, `Duplicate output field ["${fieldName}"]${transformation.sanitizeFieldNames && outputField !== fieldName
            ? ` (sanitized: ["${outputField}"])`
            : ''} for input fields: ["${matchingInputField}","${inputField}"]`);
        outputFields.set(outputField, inputField);
    });
};
const getOutputDataTapeRange = (transformation) => {
    validateRangeConfig(transformation);
    // output tape definition is decided by sanitize.
    // lets call sanitize helper to get the output tape definition.
    const sanitizeTransformation = {
        operation: 'sanitize',
        srcDataTape: {
            baseFields: transformation.fields.map((fieldDef) => {
                const { importFormat: _unUsed4, ...rest } = fieldDef;
                return rest;
            }),
            delimiter: ',',
        },
        sanitizeFieldNames: transformation.sanitizeFieldNames ?? false,
    };
    return (0, helpers_1.getSanitizeTransformOpOutputDataTape)(sanitizeTransformation);
};
const getDataRowRange = ({ sheet, decodedHeaderRange, dataEndRange, }) => {
    // data starts from the row after header.
    const startRowIndex = decodedHeaderRange.e.r + 1;
    if (dataEndRange) {
        // dataEndRange is specified.
        const decodedDataEndRange = edit_1.default.utils.decode_range(dataEndRange);
        return { startRowIndex, endRowIndex: decodedDataEndRange.s.r };
    }
    const endRowIndex = getLastDataRowIndex({ sheet, decodedHeaderRange });
    // data ends at dataEndRange if specified otherwise end of the sheetRange.
    return { startRowIndex, endRowIndex };
};
const doTransformRange = async ({ xlsBuffer, transformation, }) => {
    const { fields, sheetName, isSheetNameRegex, headerRange, dataEndRange, } = transformation;
    const sanitizeFieldNames = !!transformation.sanitizeFieldNames;
    const workbook = await (0, readWorkbook_1.readWorkbook)(xlsBuffer, {
        useDenseMode: !!transformation.useDenseMode,
    });
    const sheet = getMatchingSheet({
        workbook,
        sheetName,
        isSheetNameRegex: !!isSheetNameRegex,
    });
    const sheetRange = getDecodedSheetRange(sheet);
    (0, common_1.finleyAssert)(sheetRange, `Sheet [${sheetName}] is empty.`);
    const decodedHeaderRange = edit_1.default.utils.decode_range(headerRange);
    (0, common_1.finleyAssert)(isValidRange(decodedHeaderRange), `headerRange [${headerRange}] is not valid.`);
    (0, common_1.finleyAssert)(isWithinRange({
        cellRangeA1: headerRange,
        range: sheetRange,
    }), `headerRange [${headerRange}] is out of sheet range.`);
    const effectiveRowRange = getDataRowRange({
        sheet,
        decodedHeaderRange,
        dataEndRange,
    });
    // walk the headers and create a map of header value => column index.
    // we will use this map to associate the field definitions with the columns.
    // Note we are keeping column values as array, as header value can be repeated.
    // if a field refers to a header that is found in multiple columns
    // we will report this as a runtime error.
    const headerValueToColumns = generateHeaderToColumnsMap(decodedHeaderRange, sheet);
    const arrayLength = effectiveRowRange.endRowIndex - effectiveRowRange.startRowIndex + 1;
    // create and initialize data object. with each element an empty object
    // we allow nulls later to account from excluded rows.
    const data = Array.from({ length: arrayLength }, () => ({}));
    // initially all rows are assumed to be included.
    // for excluded rows we will replace the data[rowIndex] with null later.
    const errors = [];
    const uniqueOutputFieldNames = new Set();
    fields.forEach((entry) => {
        const { field, columnToReadFrom, importFormat, exclude, excludeIsRegex, } = entry;
        const pushNewError = (message) => errors.push({
            type: 'xlsx2CsvFieldError',
            fieldName: field,
            message,
            sheetName,
        });
        // we (just like sanitize) allow to read value from a different column (columnToReadFrom).
        // and store it with different field name.
        const columnHeaderToLookFor = columnToReadFrom ?? field;
        const outputFieldName = (0, helpers_1.getSanitizedFieldName)(field, sanitizeFieldNames);
        // This protects against input files with multiple similar headers that match after sanitization _only when_ sanitizeFieldNames is enabled
        // This should be a rare situation because most configurations using raw column lookup should not set sanitizeFieldNames to true
        if (uniqueOutputFieldNames.has(outputFieldName)) {
            pushNewError(`Field [${columnHeaderToLookFor}] has duplicate output field name (${outputFieldName}) after sanitization. Field name sanitization can be turned off to avoid the error.`);
            return;
        }
        uniqueOutputFieldNames.add(outputFieldName);
        const columnIndices = headerValueToColumns.get(columnHeaderToLookFor);
        if (!columnIndices) {
            pushNewError(`Field [${columnHeaderToLookFor}] not found in header.`);
            return;
        }
        const columnIndicesArray = Array.from(columnIndices);
        if (columnIndicesArray.length !== 1) {
            // we have multiple columns with same value.
            const columnIndicesA1 = columnIndicesArray.map((columnIndex) => edit_1.default.utils.encode_col(columnIndex));
            pushNewError(`Field [${columnHeaderToLookFor}] appears in multiple columns:${columnIndicesA1.join(',')}.`);
            return;
        }
        const columnIndex = columnIndicesArray[0];
        const effectiveStringExcludes = [];
        const effectiveRegExExcludes = [];
        const effectiveExcludes = exclude ?? [];
        for (const excludeValue of effectiveExcludes) {
            if (excludeIsRegex) {
                try {
                    const regex = new RegExp(excludeValue);
                    effectiveRegExExcludes.push(regex);
                }
                catch (err) {
                    pushNewError(`Invalid regex [${excludeValue}] for field [${outputFieldName}]`);
                    return;
                }
            }
            else {
                effectiveStringExcludes.push(excludeValue);
            }
        }
        // eslint-disable-next-line no-restricted-syntax
        for (let rowIndex = effectiveRowRange.startRowIndex; rowIndex <= effectiveRowRange.endRowIndex; 
        // eslint-disable-next-line no-plusplus
        rowIndex++) {
            const dataIndex = rowIndex - effectiveRowRange.startRowIndex;
            if (data[dataIndex] === null) {
                // this row is excluded by some other field.
                continue;
            }
            const cellAddressA1 = edit_1.default.utils.encode_cell({
                c: columnIndex,
                r: rowIndex,
            });
            const cellValue = getCellValue({
                sheet,
                cellAddressA1,
                importFormat,
            });
            if (exclude) {
                // if we are to exclude some values. see if the values
                // we got matches any of the excluded values.
                let effectiveCellValue = String(cellValue.value);
                // if value is undefined or null, we need to map to a string.
                // so that we can compare with exclude values.
                if (cellValue.value === undefined) {
                    effectiveCellValue = 'jsUndefined';
                }
                else if (cellValue.value === null) {
                    effectiveCellValue = 'jsNull';
                }
                let excludeThisRow = false;
                for (const excludeValue of effectiveStringExcludes) {
                    if (effectiveCellValue === excludeValue) {
                        excludeThisRow = true;
                        break;
                    }
                }
                for (const excludeValue of effectiveRegExExcludes) {
                    if (excludeValue.test(effectiveCellValue)) {
                        excludeThisRow = true;
                        break;
                    }
                }
                if (excludeThisRow) {
                    data[dataIndex] = null;
                    continue;
                }
            }
            const { preSanitized, value } = cellValue;
            // sanitize the field
            try {
                const sanitizedValue = (0, helpers_1.getSanitizedDataTapeField)(entry, value, preSanitized);
                const dataObject = data[dataIndex];
                (0, common_1.finleyAssert)(dataObject !== null, 'data[dataIndex] must not be null');
                dataObject[outputFieldName] = sanitizedValue;
            }
            catch (err) {
                const message = (0, typeguards_1.isInvalidField)(err)
                    ? `Cell [${cellAddressA1}] for field [${outputFieldName}] has value [${err.value}] which is invalid for expectedType [${err.expectedType}].`
                    : common_1.error.getErrorMessage(err);
                (0, common_1.debugLog)('error processing field:', JSON.stringify({
                    rowIndex,
                    columnIndex,
                    cellAddressA1,
                    cellValue,
                }, null, 4));
                errors.push({
                    type: 'xlsx2CsvCellError',
                    sheetName,
                    cell: cellAddressA1,
                    fieldName: outputFieldName,
                    message,
                });
            }
        }
    });
    if (errors.length) {
        return { isValid: false, errors };
    }
    // remove any excluded rows.
    const csvArray = data.filter(common_1.typeguards.isNotNullOrUndefined);
    return {
        isValid: true,
        data: {
            outputBuffer: common_1.csvConversion.csvArrayToFinleyBuffer({
                csvArray,
                newline: '\n', // arrayToCsvBuffer uses \r\n by default
            }),
        },
        supplementals: [],
    };
};
const validateTransform = (transformation) => {
    let tapeDefinition = null;
    try {
        switch (transformation.mode) {
            case 'extractValues':
                tapeDefinition = getOutputDataTapeValues(transformation);
                break;
            case 'convertRange':
                tapeDefinition = getOutputDataTapeRange(transformation);
                break;
            default:
                tapeDefinition = (0, common_1.finleyAssertUnreachable)(transformation);
        }
        return { isValid: true, data: tapeDefinition };
    }
    catch (error) {
        const errors = [];
        errors.push({
            type: 'metadataValidation',
            message: error instanceof Error
                ? error.message
                : 'Unexpected Error in xlsx2csv2:getOutputDataTape',
        });
        return { isValid: false, errors };
    }
};
const doTransformInternal = async ({ xlsBuffer, transformation, }) => {
    switch (transformation.mode) {
        case 'extractValues':
            return doTransformValues({
                xlsBuffer,
                transformation,
            });
        case 'convertRange':
            return doTransformRange({
                xlsBuffer,
                transformation,
            });
        default:
            return (0, common_1.finleyAssertUnreachable)(transformation);
    }
};
const performTransform = async ({ dataTapeBuffers, transformation, }) => {
    try {
        (0, common_1.finleyAssert)(dataTapeBuffers.length === 1, 'Expected 1 buffer for xlsx2csv2');
        const result = await doTransformInternal({
            xlsBuffer: dataTapeBuffers[0],
            transformation,
        });
        if (result.isValid) {
            return result;
        }
        return {
            isValid: false,
            errors: consolidateToStructuredErrors(result.errors),
        };
    }
    catch (error) {
        return (0, errorUtils_1.convertErrorToTapeFailureResult)(error, 'xlsx2csv2: ');
    }
};
exports.transformOperation = {
    operation: 'xlsx2csv2',
    validateTransform,
    performTransform,
};
//# sourceMappingURL=xlsx2csv2.js.map